// SAVE DATA dialog (Settings › ADVANCED › Export save / Import save / Restore previous save; owner-approved 2026-11-02).
// Export: summary + DOWNLOAD FILE (.json) + COPY CODE (the code is also shown, selectable, for phones / TV).
// Import: CHOOSE FILE… or paste a code → CHECK → summary of the incoming save next to the current one → REPLACE MY SAVE.
// The replaced save is kept, so RESTORE PREVIOUS SAVE undoes it. Keyboard / mouse / touch / gamepad (a controller can
// do everything except open the file picker or paste, which browsers only allow from a click / keyboard).
// Logic lives in saveTransfer.ts; this file is only the DOM.
import type { UiAction } from './input';
import { surgeForm } from '../core/surgeForms'; // SURGE FORMS: the best-form record row
import type { Profile } from './save';
import {
  ImportResult, ListKV, SaveSummary, applyImport, bundleToCode, bundleToJson, collectPrefs, exportFileName, makeBundle,
  parseImport, readPrevious, restorePrevious, summarize,
} from './saveTransfer';

export interface SaveMenuHost {
  profile(): Profile;
  kv(): ListKV | null;
  /** import / restore reload the game, so they are only offered outside a run */
  canReplace(): boolean;
  click(): void;
  deny(): void;
  close(): void;
  /** the save on disk was replaced: stop writing the old profile and reload */
  reload(note: string): void;
}

export type SaveMode = 'export' | 'import' | 'confirm' | 'restore';
interface Btn { label: string; act: () => void; primary?: boolean; danger?: boolean }

const $ = <T extends HTMLElement>(s: string) => document.querySelector(s) as T;
const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));
const n0 = (v: number) => Math.round(v).toLocaleString('en-US');
const mmss = (t: number) => `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, '0')}`;

export class SaveMenu {
  mode: SaveMode = 'export';
  private sel = 0;
  private btns: Btn[] = [];
  private pending: Extract<ImportResult, { ok: true }> | null = null;
  private code = '';

  constructor(private host: SaveMenuHost) {
    $('#sd-btns').addEventListener('click', (e) => {
      const b = (e.target as HTMLElement).closest('.btn') as HTMLElement | null; if (!b) return;
      this.sel = Number(b.dataset.i); this.press();
    });
    $('#sd-btns').addEventListener('pointerover', (e) => {
      const b = (e.target as HTMLElement).closest('.btn') as HTMLElement | null; if (!b) return;
      this.sel = Number(b.dataset.i); this.paintFocus();
    });
    $<HTMLInputElement>('#sd-file').addEventListener('change', (e) => {
      const f = (e.target as HTMLInputElement).files?.[0]; (e.target as HTMLInputElement).value = '';
      if (!f) return;
      if (f.size > 2_000_000) { this.msg('That file is too large to be a save.', true); this.host.deny(); return; }
      f.text().then((t) => this.check(t), () => this.msg('Could not read that file.', true));
    });
    $<HTMLTextAreaElement>('#sd-code').addEventListener('keydown', (e) => { if (e.key === 'Escape') { (e.target as HTMLElement).blur(); this.back(); } });
  }

  open(mode: SaveMode): void {
    this.mode = mode; this.sel = 0; this.pending = null;
    this.msg('');
    this.render();
  }

  private msg(t: string, bad = false): void { const m = $('#sd-msg'); m.textContent = t; m.classList.toggle('bad', bad); m.classList.toggle('good', !!t && !bad); }

  private summaryHtml(s: SaveSummary, title: string): string {
    const row = (k: string, v: string) => `<div class="sd-r"><span>${k}</span><b>${v}</b></div>`;
    return `<div class="sd-card"><h4>${title}</h4>`
      + row('Fracture Shards', `${n0(s.shards)} ◆`) + row('Invested in Power Ups', `${n0(s.invested)} ◆ · ${s.workshopRanks} ranks`)
      + row('Runs', `${n0(s.runs)} · ${n0(s.wins)} won`) + row('Best time', s.bestTime > 0 ? mmss(s.bestTime) : '—')
      + row('Best level · most foes', `${s.bestLevel || '—'} · ${s.bestKills ? n0(s.bestKills) : '—'}`)
      + row('Highest Surge form', s.bestForm > 0 ? `${surgeForm(s.bestForm)?.short ?? s.bestForm} · ${surgeForm(s.bestForm)?.name ?? ''}` : '—')
      + row('Encyclopedia', `${s.discovered} / ${s.discoverable} discovered`)
      + (s.exportedAt ? row('Saved', esc(new Date(s.exportedAt).toLocaleString())) : '')
      + `</div>`;
  }

  private current(): SaveSummary { const kv = this.host.kv(); return summarize(this.host.profile(), kv ? collectPrefs(kv) : {}); }

  render(): void {
    const title = $('#sd-title'), sum = $('#sd-summary'), ta = $<HTMLTextAreaElement>('#sd-code'), desc = $('#sd-desc');
    const can = this.host.canReplace();
    ta.hidden = true; ta.readOnly = false;
    if (this.mode === 'export') {
      title.textContent = 'EXPORT SAVE';
      const kv = this.host.kv(), b = makeBundle(this.host.profile(), kv ? collectPrefs(kv) : {});
      this.code = bundleToCode(b);
      const json = bundleToJson(b);
      sum.innerHTML = this.summaryHtml(this.current(), 'THIS DEVICE');
      ta.hidden = false; ta.readOnly = true; ta.value = this.code;
      desc.textContent = 'Download a backup file, or copy the code to paste on another device (Settings › Advanced › Import save). Includes Power Ups, records, Encyclopedia, button mapping and settings; graphics quality stays per device.';
      this.btns = [
        { label: 'DOWNLOAD FILE (.json)', primary: true, act: () => this.download(json) },
        { label: 'COPY CODE', act: () => this.copy() },
        { label: 'BACK', act: () => this.back() },
      ];
    } else if (this.mode === 'import') {
      title.textContent = 'IMPORT SAVE';
      sum.innerHTML = this.summaryHtml(this.current(), 'CURRENT SAVE');
      if (!can) {
        desc.textContent = 'Importing reloads the game, so it is only available from the title screen (Settings there).';
        this.btns = [{ label: 'BACK', act: () => this.back() }];
      } else {
        ta.hidden = false; if (!ta.value || ta.value === this.code) ta.value = '';
        ta.placeholder = 'Paste a save code (SLA1.…) or the contents of a save file here';
        desc.textContent = 'Choose a save file, or paste a save code and press CHECK. You will see what it contains before anything changes, and your current save is kept so you can restore it.';
        this.btns = [
          { label: 'CHOOSE FILE…', primary: true, act: () => $<HTMLInputElement>('#sd-file').click() },
          { label: 'CHECK PASTED CODE', act: () => this.check($<HTMLTextAreaElement>('#sd-code').value) },
          { label: 'BACK', act: () => this.back() },
        ];
      }
    } else if (this.mode === 'confirm' && this.pending) {
      title.textContent = 'REPLACE YOUR SAVE?';
      const p = this.pending;
      sum.innerHTML = `<div class="sd-two">${this.summaryHtml(this.current(), 'CURRENT (kept as backup)')}${this.summaryHtml(p.summary, 'INCOMING')}</div>`;
      desc.textContent = `This replaces your Power Ups, records, Encyclopedia, button mapping and ${p.summary.prefs} settings with the incoming save${p.migrated ? ` (upgraded from save version ${p.fromVersion})` : ''}. The game reloads. Settings › Advanced › Restore previous save undoes it.`;
      this.btns = [
        { label: 'REPLACE MY SAVE', danger: true, act: () => this.commitImport() },
        { label: 'CANCEL', act: () => this.back() },
      ];
    } else {
      this.mode = 'restore';
      title.textContent = 'RESTORE PREVIOUS SAVE';
      const kv = this.host.kv(), prev = kv ? readPrevious(kv) : null;
      if (!can) {
        sum.innerHTML = this.summaryHtml(this.current(), 'CURRENT SAVE');
        desc.textContent = 'Restoring reloads the game, so it is only available from the title screen (Settings there).';
        this.btns = [{ label: 'BACK', act: () => this.back() }];
      } else if (!prev) {
        sum.innerHTML = this.summaryHtml(this.current(), 'CURRENT SAVE');
        desc.textContent = 'There is no previous save yet. One is kept automatically each time you import a save.';
        this.btns = [{ label: 'BACK', act: () => this.back() }];
      } else if (!prev.ok) {
        sum.innerHTML = this.summaryHtml(this.current(), 'CURRENT SAVE');
        desc.textContent = `The previous save could not be read: ${prev.message}`;
        this.btns = [{ label: 'BACK', act: () => this.back() }];
      } else {
        sum.innerHTML = `<div class="sd-two">${this.summaryHtml(this.current(), 'CURRENT (kept as backup)')}${this.summaryHtml(prev.summary, 'PREVIOUS SAVE')}</div>`;
        desc.textContent = 'Swap back to the save that was replaced by your last import (your current save becomes the backup, so you can swap again). The game reloads.';
        this.btns = [
          { label: 'RESTORE PREVIOUS SAVE', danger: true, act: () => this.commitRestore() },
          { label: 'CANCEL', act: () => this.back() },
        ];
      }
    }
    this.sel = Math.min(this.sel, this.btns.length - 1);
    $('#sd-btns').innerHTML = this.btns.map((b, i) => `<button class="btn${b.primary ? ' primary' : ''}${b.danger ? ' danger' : ''}" data-i="${i}">${b.label}</button>`).join('');
    this.paintFocus();
  }

  private paintFocus(): void { document.querySelectorAll<HTMLElement>('#sd-btns .btn').forEach((b, k) => b.classList.toggle('focus', k === this.sel)); }
  private press(): void { this.host.click(); this.btns[this.sel]?.act(); }

  private back(): void {
    if (this.mode === 'confirm') { this.mode = 'import'; this.sel = 0; this.pending = null; this.msg(''); this.render(); return; }
    this.host.close();
  }

  private download(json: string): void {
    try {
      const url = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
      const a = document.createElement('a'); a.href = url; a.download = exportFileName(); a.style.display = 'none';
      document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 5000);
      this.msg(`Saved as ${exportFileName()} (check your downloads).`);
    } catch { this.msg('The browser blocked the download: use COPY CODE instead.', true); this.host.deny(); }
  }

  private copy(): void {
    const ta = $<HTMLTextAreaElement>('#sd-code');
    const fallback = () => {
      try { ta.focus(); ta.select(); if (document.execCommand('copy')) { this.msg('Code copied. Paste it into Import save on the other device.'); return; } } catch { /* below */ }
      ta.select(); this.msg('Copy was blocked (a controller press does not count as a click): the code is selected, copy it by hand.', true);
    };
    const c = navigator.clipboard;
    if (c?.writeText) c.writeText(this.code).then(() => this.msg('Code copied. Paste it into Import save on the other device.'), fallback); else fallback();
  }

  /** validate a file's text / a pasted code and move to the confirmation step */
  check(text: string): ImportResult {
    const r = parseImport(text);
    if (!r.ok) { this.msg(r.message, true); this.host.deny(); return r; }
    this.pending = r; this.mode = 'confirm'; this.sel = 1; // focus CANCEL first: replacing is never one stray press away
    this.msg(r.migrated ? `Save version ${r.fromVersion} — it will be upgraded on import.` : 'Save checked: integrity OK.');
    this.render();
    return r;
  }

  private commitImport(): void {
    const kv = this.host.kv(), p = this.pending;
    if (!kv || !p) { this.msg('Storage is blocked in this browser, so the save cannot be replaced.', true); return; }
    try { applyImport(kv, this.host.profile(), p); } catch { this.msg('Storage is full or blocked: nothing was changed.', true); return; }
    this.host.reload('SAVE IMPORTED · Settings › Advanced › Restore previous save undoes it');
  }

  private commitRestore(): void {
    const kv = this.host.kv();
    if (!kv || !restorePrevious(kv, this.host.profile())) { this.msg('The previous save could not be restored.', true); this.host.deny(); return; }
    this.host.reload('PREVIOUS SAVE RESTORED');
  }

  handle(a: UiAction): void {
    const n = this.btns.length;
    if (a === 'back' || a === 'pause') this.back();
    else if (a === 'down' || a === 'right') { this.sel = (this.sel + 1) % n; this.paintFocus(); this.host.click(); }
    else if (a === 'up' || a === 'left') { this.sel = (this.sel + n - 1) % n; this.paintFocus(); this.host.click(); }
    else if (a === 'confirm') this.press();
  }
}
